import React, { useState, useCallback } from 'react';
import delhiIcon from '../Assets/delhi.svg';
import mumbaiIcon from '../Assets/mumbai.svg';
import chandigarhIcon from '../Assets/chandigarh.svg';
import hyderabadIcon from '../Assets/hyderabad.svg';
import ahmedabadIcon from '../Assets/ahemdabad.svg';
import kolkataIcon from '../Assets/kolkata.svg';
import chennaiIcon from '../Assets/chennai.svg';
import bengaluruIcon from '../Assets/bengaluru.svg';
import puneIcon from '../Assets/pune.svg';
import { POPULAR_INDIAN_CITIES, ALL_INDIAN_CITIES } from '../data/indianCities';
import { saveUserLocation, reverseGeocode, findNearestCity } from '../utils/userLocation';
import useUserLocation from '../hooks/useUserLocation';

const cityIcons = {
  delhi_ncr_logo: delhiIcon,
  hyderabad_logo: hyderabadIcon,
  chennai_logo: chennaiIcon,
  mumbai_logo: mumbaiIcon,
  ahmedabad_logo: ahmedabadIcon,
  bengaluru_logo: bengaluruIcon,
  chandigarh_logo: chandigarhIcon,
  kolkata_logo: kolkataIcon,
  pune_logo: puneIcon,
};

const CitySelectionModal = ({ isOpen, onClose, onCitySelect, canClose = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('IDLE'); // 'IDLE' | 'LOCATION_FETCHING'
  const [locationError, setLocationError] = useState('');
  const userLocation = useUserLocation();

  const isFetching = status === 'LOCATION_FETCHING';

  // Search filters only the all-cities list (case-insensitive substring);
  // the popular grid never changes.
  const filteredAllCities = ALL_INDIAN_CITIES.filter((city) =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Path A — manual selection: labels + the city's hardcoded coordinates.
  const handleCityClick = (city) => {
    saveUserLocation({
      state: city.state,
      city: city.name,
      pincode: "250110", // placeholder, same as mobile
      latitude: city.latitude,
      longitude: city.longitude,
    });
    onCitySelect?.(city.name);
    setLocationError('');
    onClose();
  };

  // Path B — auto-detect: reverse-geocoded labels + the user's EXACT GPS
  // coordinates (never snapped to a city's coordinates).
  const autoDetectLocation = useCallback(async () => {
    if (!("geolocation" in navigator)) {
      setLocationError("Location is not supported by this browser.");
      return;
    }

    setStatus('LOCATION_FETCHING');
    setLocationError('');
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
        })
      );

      const { latitude, longitude } = pos.coords;

      let place;
      try {
        place = await reverseGeocode(latitude, longitude);
      } catch {
        const nearest = findNearestCity(latitude, longitude);
        place = { city: nearest.name, state: nearest.state, pincode: "" };
      }

      saveUserLocation({
        state: place.state,
        city: place.city,
        pincode: place.pincode,
        latitude,
        longitude,
      });
      onCitySelect?.(place.city);
      onClose();
    } catch (error) {
      if (error && error.code === 1) { // PERMISSION_DENIED
        setLocationError("Location access denied. Enable location permission for this site in your browser settings.");
      } else {
        setLocationError("Couldn't fetch location. Please try again or select your city manually.");
      }
    } finally {
      setStatus('IDLE');
    }
  }, [onClose, onCitySelect]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-[1000] bg-black bg-opacity-80"
      style={{
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      <div
        className="bg-[#111111] rounded-3xl p-6 relative text-white flex flex-col overflow-hidden shadow-2xl"
        style={{
          width: '100%',
          maxWidth: '640px',
          height: '500px'
        }}
      >
        {canClose && (
          <button
            className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 transition-colors z-10"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        )}

        <h2 className="text-xl font-semibold mb-4">Select Your City</h2>

        {/* Search Bar */}
        <div className="mb-4 flex items-center bg-[#2A2A2A] px-4 py-3 rounded-lg">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search your city"
            className="bg-transparent text-white outline-none flex-1 placeholder-gray-400"
          />
          <button
            onClick={autoDetectLocation}
            disabled={isFetching}
            title="Detect current location"
            className={`ml-3 px-3 py-1 rounded transition-all ${
              isFetching
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-400 hover:text-blue-300 hover:bg-blue-400/10'
            }`}
          >
            {isFetching ? (
              <div className="animate-spin h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full"></div>
            ) : (
              '📍'
            )}
          </button>
        </div>

        {/* Location Error Message */}
        {locationError && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-red-600/20 text-red-300 border border-red-600/30">
            {locationError}
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Popular Cities */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-3">Popular Cities</h3>
            <div className="grid grid-cols-5 gap-3">
              {POPULAR_INDIAN_CITIES.map((city) => {
                const isSelected = userLocation?.city === city.name;
                return (
                  <div
                    key={city.name}
                    className="bg-[#2A2A2A] rounded-lg p-3 flex flex-col items-center justify-center hover:bg-[#3A3A3A] cursor-pointer transition-all hover:scale-105"
                    onClick={() => handleCityClick(city)}
                  >
                    <img
                      src={cityIcons[city.icon]}
                      alt={city.name}
                      className="h-8 w-8 mb-2"
                      style={isSelected ? { filter: 'brightness(0) saturate(100%) invert(46%) sepia(85%) saturate(2007%) hue-rotate(101deg) brightness(102%) contrast(98%)' } : {}}
                    />
                    <p className={`text-sm text-center`} style={isSelected ? { color: '#17BD3B' } : {}}>{city.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* All Cities */}
          {filteredAllCities.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-3">All Cities</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {filteredAllCities.map((city) => {
                  const isSelected = userLocation?.city === city.name;
                  return (
                    <button
                      key={city.name}
                      className={`text-left p-2 rounded transition-colors hover:bg-[#2A2A2A] ${
                        isSelected
                          ? ''
                          : 'text-gray-300 hover:text-white'
                      }`}
                      style={isSelected ? { color: '#17BD3B' } : {}}
                      onClick={() => handleCityClick(city)}
                    >
                      {city.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchTerm && filteredAllCities.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              <p>No cities found matching "{searchTerm}"</p>
              <p className="text-sm mt-2">Try a different search term or use location detection</p>
            </div>
          )}
        </div>

        {/* Fetching overlay — blocks interaction until the attempt resolves */}
        {isFetching && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin h-8 w-8 border-2 border-[#17BD3B] border-t-transparent rounded-full"></div>
              <p className="text-white text-sm animate-pulse">Fetching Location...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitySelectionModal;
